import { PageHeader } from "../../../Shared/PageHeader";
import YesNoModal from "../../../Shared/YesNoModal";
import ChangeReasonModal from "../../../Shared/ChangeReasonModal";
import { useRecipeForm } from "../../../Hooks/useRecipeForm";
import { RecipeFormFields } from "./RecipeFormFields";
import { RecipeFormActions } from "./RecipeFormActions";
import { RecipeValidationSummary } from "./RecipeValidationSummary";
import OptimizeRecipeModal from "./OptimizeRecipeModal";
import { AiSingleStarIcon } from "../../../Shared/AiSingleStarIcon";
import React, {
	useState,
	useEffect,
	useRef
} from "react";
import {
	Paper,
	Divider,
	LinearProgress
} from "@material-ui/core";
import { useImages } from "../../../Hooks/useImages";
import { RecipeImagesUpload } from "../../../Shared/RecipeImagesUpload";
import { RecipeImagesGallery } from "../../../Shared/RecipeImagesGallery";
import { downloadImagesAsFiles } from "../../../Helpers/imageHelper";

const LARGE_DURATION_RANGE = [1.7, 2.6];
const MEDIUM_DURATION_RANGE = [0.9, 1.4];
const SMALL_DURATION_RANGE = [0.5, 0.8];
const LARGE_DELAY_RANGE = [0, 0.7];
const MEDIUM_DELAY_RANGE = [0, 0.5];
const SMALL_DELAY_RANGE = [0, 0.4];

export function RecipeForm(props) {
	const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
	const [isChangeReasonModalOpen, setIsChangeReasonModalOpen] = useState(false);
	const [changeReason, setChangeReason] = useState("");
	const [isOptimizeModalOpen, setIsOptimizeModalOpen] = useState(false);
	const [optimizeInstructions, setOptimizeInstructions] = useState("");
	const [isOptimizing, setIsOptimizing] = useState(false);
	const [previousRecipe, setPreviousRecipe] = useState(null);
	const [streamOutput, setStreamOutput] = useState("");
	const [starAnimations, setStarAnimations] = useState([]);
	const optimizeSocketRef = useRef(null);
	const recipeForm = useRecipeForm(props.recipe);
	const images = useImages(props.config);
	const [imageItems, setImageItems] = useState([]); // { id?, url?, file?, previewUrl? }
	const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
	const [isSavingImages, setIsSavingImages] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [processedImageUrls, setProcessedImageUrls] = useState(null); // Track which ImageUrls we've processed

	const recipeId = props.recipe?.recipeId;
	const apiUrl = props.config?.apiUrl;

	useEffect(() => {
		if (!recipeId) return;
		let isMounted = true;
		images.list(recipeId).then((ids) => {
			if (isMounted) {
				setImageItems(ids.map((id) => ({
					id: id,
					url: `${apiUrl}/Recipes/${recipeId}/Images/${id}`
				})));
				setPendingDeleteIds([]);
			}
		});
		return () => { isMounted = false; };
		// Only reload images when recipeId changes, not when apiUrl or images object changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [recipeId]);

	useEffect(() => {
		return () => {
			if (optimizeSocketRef.current) {
				optimizeSocketRef.current.close();
				optimizeSocketRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		if (!recipeId) {
			setImageItems([]);
			setPendingDeleteIds([]);
		}
	}, [recipeId]);

	useEffect(() => {
		if (props.resetSignal == null) return;
		setImageItems([]);
		setPendingDeleteIds([]);
		setUploadProgress(0);
		setProcessedImageUrls(null);
	}, [props.resetSignal]);

	// Handle imported ImageUrls from scraped recipes
	useEffect(() => {
		const recipe = props.recipe;
		if (!recipe) {
			return;
		}

		const imageUrls = recipe.imageUrls;
		if (!imageUrls || !Array.isArray(imageUrls) || imageUrls.length === 0) {
			return;
		}

		// Only process if this is a new recipe (no recipeId)
		if (recipe.recipeId) {
			return;
		}

		// Prevent re-processing the same ImageUrls
		// Simple comparison: if we've processed any URLs, don't process again
		// This assumes imageUrls only exist on imported recipes and won't change
		if (processedImageUrls !== null) {
			return;
		}

		let isMounted = true;

		async function downloadImages() {
			try {
				const downloadedImages = await downloadImagesAsFiles(imageUrls, props.config.apiUrl);
				if (isMounted && downloadedImages.length > 0) {
					setImageItems(prev => [...prev, ...downloadedImages]);
				}
				// Mark as processed regardless of success/failure to prevent retries
				if (isMounted) {
					setProcessedImageUrls(true);
				}
			} catch (error) {
				console.error('Error downloading imported images:', error);
			}
		}

		downloadImages();

		return () => {
			isMounted = false;
		};
	}, [props.recipe, processedImageUrls, props.config.apiUrl]); // eslint-disable-line react-hooks/exhaustive-deps

	function buildWebSocketUrl(apiUrl, path, trackingId) {
		try {
			const url = new URL(apiUrl);
			url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
			url.pathname = `${url.pathname.replace(/\/$/, "")}${path}`;
			url.search = `trackingId=${encodeURIComponent(trackingId)}`;
			return url.toString();
		} catch (e) {
			return `${apiUrl.replace(/^http/, "ws")}${path}?trackingId=${encodeURIComponent(trackingId)}`;
		}
	}

	function onFilesSelected(files) {
		const previews = files.map((file) => ({
			file,
			previewUrl: URL.createObjectURL(file)
		}));
		setImageItems((prev) => [...prev, ...previews]);
	}

	function onDeleteImage(item) {
		if (item.file) {
			setImageItems((prev) => prev.filter((x) => x !== item));
			return;
		}

		if (item.id != null && props.recipe?.recipeId) {
			setPendingDeleteIds((prev) => [...prev, item.id.toString()]);
		}
		setImageItems((prev) => prev.filter((x) => x !== item));
	}

	function onReorderImages(nextOrder) {
		setImageItems(nextOrder);
	}

	function createTrackingId() {
		return `trk-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 8)}`;
	}

	function randomInRange([min, max]) {
		return Math.random() * (max - min) + min;
	}

	function startOptimizeStream(trackingId) {
		if (!trackingId || !props.config?.apiUrl)
			return;

		const wsUrl = buildWebSocketUrl(props.config.apiUrl, "/ws/ai-optimization", trackingId);
		try {
			const socket = new WebSocket(wsUrl);
			optimizeSocketRef.current = socket;
			setStreamOutput("Connecting to AI stream...");
			socket.onmessage = (evt) => {
				const message = typeof evt.data === "string" ? evt.data : "";
				setStreamOutput((prev) => prev ? `${prev}\n${message}` : message);
			};
			socket.onerror = () => {
				setStreamOutput((prev) => prev ? `${prev}\nStream error.` : "Stream error.");
			};
			socket.onclose = () => {
				optimizeSocketRef.current = null;
			};
		} catch (err) {
			setStreamOutput("Unable to connect to AI stream.");
		}
	}

	function stopOptimizeStream() {
		if (optimizeSocketRef.current) {
			optimizeSocketRef.current.close();
			optimizeSocketRef.current = null;
		}
	}

	function onNameChange(e) {
		recipeForm.handleNameChange(e.target.value);
	}

	function onTagsChange(e) {
		recipeForm.handleTagsChange(e.target.value);
	}

	function onNotesChange(e) {
		recipeForm.handleNotesChange(e.target.value);
	}

	function onIngredientsChange(e) {
		recipeForm.handleIngredientsChange(e.target.value);
	}

	function onInstructionsChange(e) {
		recipeForm.handleInstructionsChange(e.target.value);
	}

	function onCancelModalYes() {
		setIsCancelModalOpen(false);
		recipeForm.reset();
		props.onCancel();
	}

	function onCancelModalNo() {
		setIsCancelModalOpen(false);
	}

	function onCancelClick() {
		setIsCancelModalOpen(true);
	}

	function getSaveErrorMessage(err) {
		const responseMessage = err?.response?.data?.message || err?.response?.data?.title;
		return responseMessage || "Unable to save the recipe. Please try again.";
	}

	async function onSaveClick() {
		if (!recipeForm.isValid())
			return;

		// Only show change reason modal if we're editing an existing recipe
		if (props.recipe?.recipeId) {
			setChangeReason(generateChangeReason(props.recipe, recipeForm.recipe, imageItems, pendingDeleteIds));
			setIsChangeReasonModalOpen(true);
		} else {
			performSave("");
		}
	}

	async function performSave(reason) {
		setIsChangeReasonModalOpen(false);
		setIsSavingImages(true);

		// Snapshot image state before async work so parent re-renders cannot clear it
		const currentImageItems = [...imageItems];
		const currentPendingDeleteIds = [...pendingDeleteIds];

		// Save recipe first to get the ID (required before uploading images)
		let savedRecipeId;
		try {
			const { ingredientsList, imageOrderCsv, ...recipeWithoutList } = recipeForm.recipe;
			if (!recipeWithoutList.recipeId) {
				delete recipeWithoutList.recipeId;
			}
			const saveResult = props.onSaveClick(recipeWithoutList, reason);
			const resolved = await Promise.resolve(saveResult);
			savedRecipeId = props.recipe?.recipeId || resolved?.recipeId || resolved?.data || resolved;
		} catch (err) {
			console.error(err);
			setIsSavingImages(false);
			alert(getSaveErrorMessage(err));
			return;
		}

		if (!savedRecipeId) {
			console.error("Recipe save did not return an id.");
			setIsSavingImages(false);
			alert("Unable to save the recipe. Please try again.");
			return;
		}

		try {
			if (currentPendingDeleteIds.length > 0) {
				await Promise.all(currentPendingDeleteIds.map((id) => images.remove(savedRecipeId, id)));
				setPendingDeleteIds([]);
			}

			const newFileItems = currentImageItems.filter((i) => i.file);
			let uploadedItems = [];
			if (newFileItems.length > 0) {
				setUploadProgress(0);
				const uploadOrderFiles = newFileItems.map((i) => i.file);
				const newIds = await images.upload(savedRecipeId, uploadOrderFiles, (evt) => {
					if (!evt.total) return;
					setUploadProgress(Math.round((evt.loaded * 100) / evt.total));
				});
				const base = `${props.config.apiUrl}/Recipes/${savedRecipeId}/Images/`;
				uploadedItems = (newIds || []).map((id) => ({ id, url: `${base}${id}` }));
			}

			let uploadIdx = 0;
			const mergedItems = currentImageItems.map((item) => {
				if (item.file) {
					const mapped = uploadedItems[uploadIdx++];
					return mapped ? mapped : null;
				}
				return item;
			}).filter(Boolean);

			setImageItems(mergedItems);
			setPendingDeleteIds([]);
			setUploadProgress(0);

			const orderedIds = mergedItems.filter((i) => i.id != null).map((i) => i.id);
			if (orderedIds.length > 0) {
				await images.reorder(savedRecipeId, orderedIds);
			}

			if (props.onAfterSave) {
				await Promise.resolve(props.onAfterSave(savedRecipeId));
			}
		} catch (err) {
			console.error(err);
			alert("Unable to save images. Please try again.");
		} finally {
			setIsSavingImages(false);
			setUploadProgress(0);
		}
	}

	function onOptimizeClick() {
		if (!props.onOptimize) {
			return;
		}
		setOptimizeInstructions("");
		setStreamOutput("");
		setIsOptimizeModalOpen(true);
	}

	function onOptimizeCancel() {
		setIsOptimizeModalOpen(false);
		setIsOptimizing(false);
		setStarAnimations([]);
		stopOptimizeStream();
	}

	function onUndoOptimize() {
		if (previousRecipe) {
			recipeForm.applyRecipe(previousRecipe);
			setPreviousRecipe(null);
		}
	}

	function onOptimizeSubmit() {
		if (!props.onOptimize || isOptimizing) return;

		setPreviousRecipe(JSON.parse(JSON.stringify(recipeForm.recipe)));
		setIsOptimizing(true);
		setStreamOutput("");

		const trackingId = createTrackingId();
		startOptimizeStream(trackingId);

		const starsCount = 12;
		const newStars = [];
		for (let i = 0; i < starsCount; i++) {
			newStars.push({
				id: `star-${Date.now()}-${i}`,
				largeDuration: randomInRange(LARGE_DURATION_RANGE),
				largDelay: randomInRange(LARGE_DELAY_RANGE),
				mediumDuration: randomInRange(MEDIUM_DURATION_RANGE),
				mediumDelay: randomInRange(MEDIUM_DELAY_RANGE),
				smallDuration: randomInRange(SMALL_DURATION_RANGE),
				smallDelay: randomInRange(SMALL_DELAY_RANGE)
			});
		}
		setStarAnimations(newStars);

		const optimizeRequest = {
			Recipe: recipeForm.recipe,
			AdditionalInstructions: optimizeInstructions,
			TrackingId: trackingId
		};

		props.onOptimize(optimizeRequest)
			.then((data) => {
				if (data) {
					recipeForm.applyRecipe({ ...recipeForm.recipe, ...data });
				}
			})
			.catch((err) => {
				console.error(err);
			})
			.finally(() => {
				setIsOptimizing(false);
				setIsOptimizeModalOpen(false);
				stopOptimizeStream();
				setTimeout(() => setStarAnimations([]), 3000);
			});
	}

	return (
		<React.Fragment>
			<PageHeader text={props.pageTitle} config={props.config} />
			{isOptimizing && starAnimations.length > 0 ? (
				<div className="ai-optimizing-overlay">
					<div className="ai-overlay-stars">
						{starAnimations.map((timings) => {
							return (
								<div key={timings.id} className="ai-star-group">
									<AiSingleStarIcon
										className="ai-single-star"
										style={{ animationDuration: `${timings.largeDuration}s`, animationDelay: `${timings.largDelay}s` }}
									/>
									<AiSingleStarIcon
										className="ai-single-star ai-star-medium"
										style={{ animationDuration: `${timings.mediumDuration}s`, animationDelay: `${timings.mediumDelay}s` }}
									/>
									<AiSingleStarIcon
										className="ai-single-star ai-star-small"
										style={{ animationDuration: `${timings.smallDuration}s`, animationDelay: `${timings.smallDelay}s` }}
									/>
								</div>
							);
						})}
					</div>
				</div>
			) : null}
			<Paper style={{ padding: 12 }}>
				<RecipeValidationSummary errors={recipeForm.errors} />
				<RecipeFormFields
					recipe={recipeForm.recipe}
					tagOptions={props.tagOptions}
					errors={recipeForm.errors}
					onNameChange={onNameChange}
					onTagsChange={onTagsChange}
					onNotesChange={onNotesChange}
					onIngredientsChange={onIngredientsChange}
					onInstructionsChange={onInstructionsChange}
					disabled={props.isSaveExecuting || isOptimizing} />
				<Divider className="rb-divider" />
				{(props.isSaveExecuting || isOptimizing) ? (<LinearProgress />) : (null)}
				<RecipeImagesUpload
					config={props.config}
					onFilesSelected={onFilesSelected}
					disabled={isSavingImages || props.isSaveExecuting} />
				<RecipeImagesGallery
					images={imageItems}
					onDelete={onDeleteImage}
					onReorder={onReorderImages}
					disabled={props.isSaveExecuting || isOptimizing} />
				<RecipeFormActions
					config={props.config}
					onSaveClick={onSaveClick}
					onCancelClick={onCancelClick}
					onImportClick={props.onImportClick}
					onOptimizeClick={props.onOptimize ? onOptimizeClick : null}
					isExecuting={props.isSaveExecuting || isOptimizing}
					canUndo={!!previousRecipe}
					onUndoClick={onUndoOptimize} />
				<YesNoModal
					isOpen={isCancelModalOpen}
					title="Cancel Changes"
					question="Are you sure you want to cancel all changes?"
					onYes={onCancelModalYes}
					onNo={onCancelModalNo} />
				<ChangeReasonModal
					isOpen={isChangeReasonModalOpen}
					reason={changeReason}
					onReasonChange={(e) => setChangeReason(e.target.value)}
					onSave={() => performSave(changeReason)}
					onCancel={() => setIsChangeReasonModalOpen(false)} />
				<OptimizeRecipeModal
					isOpen={isOptimizeModalOpen}
					instructions={optimizeInstructions}
					onInstructionsChange={setOptimizeInstructions}
					streamOutput={streamOutput}
					isStreaming={isOptimizing}
					onSubmit={onOptimizeSubmit}
					onCancel={onOptimizeCancel}
					isExecuting={isOptimizing} />
				{isSavingImages && uploadProgress > 0 ? (
					<LinearProgress variant="determinate" value={uploadProgress} style={{ marginTop: 4 }} />
				) : null}
			</Paper>
		</React.Fragment >
	);
}

function generateChangeReason(originalRecipe, currentRecipe, currentImageItems, currentPendingDeleteIds) {
	const parts = [];

	if ((originalRecipe?.name || "") !== (currentRecipe?.name || "")) {
		parts.push("changed title");
	}

	const origTags = originalRecipe?.tags || [];
	const newTags = currentRecipe?.tags || [];
	const addedTags = newTags.filter(t => !origTags.includes(t));
	const removedTags = origTags.filter(t => !newTags.includes(t));
	if (addedTags.length > 0 || removedTags.length > 0) {
		if (addedTags.length === 1 && removedTags.length === 0) {
			parts.push(`added "${addedTags[0]}" tag`);
		}
		else if (addedTags.length > 0 && removedTags.length === 0) {
			parts.push("added tags");
		}
		else if (removedTags.length === 1 && addedTags.length === 0) {
			parts.push(`removed "${removedTags[0]}" tag`);
		}
		else if (removedTags.length > 0 && addedTags.length === 0) {
			parts.push("removed tags");
		}
		else {
			parts.push("changed tags");
		}
	}

	if ((originalRecipe?.ingredients || "") !== (currentRecipe?.ingredients || "")) {
		parts.push("changed ingredients");
	}

	if ((originalRecipe?.instructions || "") !== (currentRecipe?.instructions || "")) {
		parts.push("changed instructions");
	}

	if ((originalRecipe?.notes || "") !== (currentRecipe?.notes || "")) {
		parts.push("changed notes");
	}

	const newImagesCount = currentImageItems.filter(i => i.file).length;
	const deletedImagesCount = currentPendingDeleteIds.length;
	if (newImagesCount > 0 && deletedImagesCount === 0) {
		parts.push(newImagesCount === 1 ? "added an image" : "added images");
	}
	else if (deletedImagesCount > 0 && newImagesCount === 0) {
		parts.push(deletedImagesCount === 1 ? "removed an image" : "removed images");
	}
	else if (newImagesCount > 0 || deletedImagesCount > 0) {
		parts.push("changed images");
	}

	return parts.join(", ");
}
